import type {
  ContactBlockSource,
  LeadStatus,
  MessageCampaignStatus,
  Tone,
  WhatsappStatus,
} from '@kardume/types';

/**
 * Rótulos e cores compartilhados entre a listagem de campanhas e o drawer de
 * detalhe.
 *
 * Num arquivo só porque os dois mostram o mesmo status lado a lado: duplicar o mapa
 * garantiria que um dia a listagem diria "Pausada" e o drawer "Em andamento" para a
 * mesma campanha.
 */

export const CAMPAIGN_STATUS_TONE: Record<MessageCampaignStatus, Tone> = {
  DRAFT: 'secondary',
  RUNNING: 'info',
  PAUSED: 'warning',
  COMPLETED: 'success',
  CANCELED: 'danger',
};

export const CAMPAIGN_STATUS_LABEL: Record<MessageCampaignStatus, string> = {
  DRAFT: 'Rascunho',
  RUNNING: 'Em andamento',
  PAUSED: 'Pausada',
  COMPLETED: 'Concluída',
  CANCELED: 'Cancelada',
};

export const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  NEW: 'Novo',
  CONTACTED: 'Contatado',
  QUALIFIED: 'Qualificado',
  NEGOTIATING: 'Negociando',
  WON: 'Ganho',
  LOST: 'Perdido',
};

export const LEAD_STATUSES = Object.keys(LEAD_STATUS_LABEL) as LeadStatus[];

/**
 * Estado da verificação de WhatsApp.
 *
 * `UNKNOWN` é "ainda na fila do varredor", não "deu erro" - dizer "Desconhecido" faria
 * o operador procurar um problema que não existe, então a tela diz "verificando…".
 */
export const WHATSAPP_STATUS_LABEL: Record<WhatsappStatus, string> = {
  UNKNOWN: 'Verificando…',
  VALID: 'WhatsApp ok',
  INVALID: 'Sem WhatsApp · não enviar',
};

export const WHATSAPP_STATUS_TONE: Record<WhatsappStatus, Tone> = {
  UNKNOWN: 'secondary',
  VALID: 'success',
  INVALID: 'danger',
};

/** Quem bloqueou. Separa o que a máquina decidiu do que alguém decidiu. */
export const BLOCK_SOURCE_LABEL: Record<ContactBlockSource, string> = {
  SEND_FAILURE: 'Falha de envio',
  INVALID_NUMBER: 'Número sem WhatsApp',
  MANUAL: 'Bloqueio manual',
};

/**
 * Selo da loja parceira, usado na aba Contatos e dentro da lista aberta.
 *
 * Aqui, e não em cada tela, porque parceiro é um recorte - não um impedimento. As duas
 * telas precisam dizer a mesma coisa com a mesma palavra, senão uma diria "Parceiro" e a
 * outra "Parceria ativa" para o mesmo lojista.
 */
export const PARTNER_BADGE_LABEL = 'Parceiro';

/**
 * Um número em `INVALID` ou bloqueado não deve receber, e o pedido da spec é ver isso
 * sem procurar. As duas telas (aba Contatos e a lista aberta) usam o mesmo teste para
 * não divergirem sobre o que é uma linha em evidência.
 */
export const isUndeliverable = (input: {
  blocked?: boolean;
  whatsappStatus?: WhatsappStatus;
}): boolean => Boolean(input.blocked) || input.whatsappStatus === 'INVALID';
